/**
 * Which report tokens carry markup rather than data.
 *
 * `fillTokens` escapes every value by default, so anything the report builds as
 * HTML itself — table rows, the disclaimer block, the conditional customer-note
 * callout — has to be named here or it would be printed as visible tag soup.
 *
 * Shared because both the server (filling the report template) and the send
 * dialog (filling the covering email, which may embed the comparison table)
 * pass the same list.
 */
export const RAW_HTML_TOKENS = [
  'comparison_table_rows',
  'invoice_line_item_rows',
  'disclaimer_paragraphs',
  'customer_note_block',
];

/**
 * The tokens a covering-email template can use, grouped for the picker in the
 * template editor. Kept in step with `bvReportContextService.buildBillReportData`
 * — anything listed here must be a key that service produces, or the editor is
 * advertising placeholders that will render literally in a client's inbox.
 */
export const EMAIL_TOKEN_GROUPS: { group: string; tokens: { key: string; label: string }[] }[] = [
  {
    group: 'Client & site',
    tokens: [
      { key: 'client_name', label: 'Client name' },
      { key: 'client_abn', label: 'Client ABN' },
      { key: 'site_name', label: 'Site name' },
      { key: 'nmi', label: 'NMI' },
      { key: 'site_address', label: 'Site address' },
      { key: 'retailer_name', label: 'Retailer' },
    ],
  },
  {
    group: 'Period',
    tokens: [
      { key: 'billing_period', label: 'Billing period' },
      { key: 'billing_period_from', label: 'Period from' },
      { key: 'billing_period_to', label: 'Period to' },
      { key: 'billing_days', label: 'Billing days' },
      { key: 'report_date', label: 'Report date' },
    ],
  },
  {
    group: 'Headline figures',
    tokens: [
      { key: 'expected_total', label: 'Expected total' },
      { key: 'invoiced_total', label: 'Retailer total' },
      { key: 'variance_dollars', label: 'Variance $' },
      { key: 'variance_percent', label: 'Variance %' },
      { key: 'accuracy_percent', label: 'Accuracy %' },
      { key: 'status_label', label: 'Status (OVERCHARGED…)' },
      { key: 'total_kwh', label: 'Total kWh' },
    ],
  },
  {
    group: 'By section',
    tokens: [
      { key: 'retail_expected', label: 'Retail expected' },
      { key: 'retail_invoiced', label: 'Retail invoiced' },
      { key: 'retail_variance', label: 'Retail variance' },
      { key: 'network_expected', label: 'Network expected' },
      { key: 'network_invoiced', label: 'Network invoiced' },
      { key: 'network_variance', label: 'Network variance' },
      { key: 'metering_variance', label: 'Metering variance' },
      { key: 'environmental_variance', label: 'Environmental variance' },
      { key: 'gst_expected', label: 'GST expected' },
      { key: 'gst_invoiced', label: 'GST invoiced' },
    ],
  },
  {
    group: 'Narrative & blocks',
    tokens: [
      { key: 'overcharge_summary', label: 'Verdict sentence' },
      { key: 'validation_summary', label: 'Validation summary' },
      { key: 'customer_note', label: 'Adviser note' },
      { key: 'comparison_table_rows', label: 'Comparison table rows (HTML)' },
    ],
  },
];
